import React, { useState } from "react";

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [strength, setStrength] = useState(0);
  const [show, setShow] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Simple password strength score
  
  const passwordStrengthScore = (pw) => {
    let score = 0;
    if (!pw) return 0;
    score += Math.min(10, pw.length) * 2;
    if (/[a-z]/.test(pw)) score += 10;
    if (/[A-Z]/.test(pw)) score += 10;
    if (/[0-9]/.test(pw)) score += 10;
    if (/[^A-Za-z0-9]/.test(pw)) score += 10;
    const common = ["1234", "password", "qwerty", "abcd", "1111"];
    for (const c of common)
      if (pw.toLowerCase().includes(c)) score -= 20;
    return Math.max(0, Math.min(60, score));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setMessage("");

    let valid = true;
    const newErrors = {};

    if (!currentPassword.trim()) {
      newErrors.current = "Please enter your current password.";
      valid = false;
    }
    if (!newPassword || newPassword.length < 8) {
      newErrors.new = "New password must be at least 8 characters.";
      valid = false;
    }
    if (newPassword === currentPassword) {
      newErrors.new =
        "New password must be different from current password.";
      valid = false;
    }
    if (confirmPassword !== newPassword) {
      newErrors.confirm = "Passwords do not match.";
      valid = false;
    }

    if (!valid) {
      setErrors(newErrors);
      return;
    }

    try {
      setMessage("Updating...");

      // Example API call (replace with your own endpoint)
      
      const response = await fetch("/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (!response.ok) {
        const data = await response.json();
        setMessage(data.message || "Failed to update password.");
        return;
      }

      setMessage("Password updated successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setStrength(0);
    } catch (error) {
      console.error(error);
      setMessage("Network error. Please try again.");
    }
  };

  const handleStrength = (value) => {
    setNewPassword(value);
    setStrength(passwordStrengthScore(value));
  };

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrors({});
    setMessage("");
    setStrength(0);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={{ marginBottom:"6px", fontFamily:"Roboto, sans-serif", }}>Change Password</h2>
         <hr style={{marginBottom:"4px", marginTop:"4px", width:"42px", border:"1px solid #7b7b7d", opacity:"2", background:"linear-gradient(90deg,rgba(47, 159, 212, 1) 0%, rgba(10, 50, 69, 1) 59%, rgba(53, 118, 148, 1) 100%)" }}/>
         <p style={{ color: "#6b7280", fontSize:14, marginBottom: 18 }}>
            For security, choose a strong new password and don’t reuse one from another site.
         </p>

        <form onSubmit={handleSubmit}>

          {/*-------- Current Password -------*/}

          <div style={styles.field}>
            <label style={styles.label}> Current Password </label>
            <div style={styles.inputWrap}>
              <input
                type={show.current ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                minLength={6}
                style={styles.input}
              />
              <button
                type="button"
                style={styles.toggle}
                onClick={() =>
                  setShow((s) => ({ ...s, current: !s.current }))
                }
               >
                {show.current ? "Hide" : "Show"}
              </button>
            </div>
               {errors.current && <p style={styles.error}>{errors.current}</p>}
          </div>

          {/*------- New Password ------*/}

          <div style={styles.field}>
            <label style={styles.label}> New Password </label>
            <div style={styles.inputWrap}>
              <input
                type={show.new ? "text" : "password"}
                value={newPassword}
                onChange={(e) => handleStrength(e.target.value)}
                required
                minLength={8}
                style={styles.input}
              />
              <button
                type="button"
                style={styles.toggle}
                onClick={() => setShow((s) => ({ ...s, new: !s.new }))}
              >
                {show.new ? "Hide" : "Show"}
              </button>
            </div>
            <p style={styles.note}>
              Use at least 8 characters. Mix letters, numbers, and symbols.
            </p>
            <div style={styles.meter}>
              <div
                style={{
                  ...styles.bar,
                  width: `${Math.round((strength / 60) * 100)}%`,
                }}
              ></div>
            </div>
            {errors.new && <p style={styles.error}>{errors.new}</p>}
          </div>

          {/* Confirm Password */}
          
          <div style={styles.field}>
            <label style={styles.label}>Confirm New Password</label>
            <div style={styles.inputWrap}>
              <input
                type={show.confirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                style={styles.input}
              />
              <button
                type="button"
                style={styles.toggle}
                onClick={() =>
                  setShow((s) => ({ ...s, confirm: !s.confirm }))
                }
              >
                {show.confirm ? "Hide" : "Show"}
              </button>
            </div>
            {errors.confirm && <p style={styles.error}>{errors.confirm}</p>}
          </div>

          {/* Actions */}
          <div style={styles.actions}>
            <div>
              <button type="submit" style={styles.primaryBtn}>
                Update Password
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={styles.ghostBtn}
              >
                Reset
              </button>
            </div>
            {message && <p>{message}</p>}
          </div>
        </form>
      </div>
    </div>
  );
};

// Inline Styles (you can move this into a CSS file)

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "#f7f7fb",
    padding: 24,
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 6px 20px rgba(11,12,16,0.08)",
    padding: 24,
    width: "100%",
    maxWidth: 370,
  },
  field: { marginBottom: 14 },
  label: { display: "block", marginBottom: 6, fontWeight: 600, fontSize: 15, fontFamily: "Roboto, sans-serif"   },
  inputWrap: { position: "relative" },
  input: {
    width: "100%",
    padding: "10px 40px 10px 12px",
    borderRadius: 8,
    border: "1px solid #e6e8ee",
    fontSize: 14,
    outline: "none",
  },
  toggle: {
    position: "absolute",
    right: 6,
    top: 6,
    padding: "6px 10px",
    background: "transparent",
    border: 0,
    cursor: "pointer",
    color: "#6b7280",
    fontSize: 13,
  },
  note: { fontSize: 13, color: "#6b7280" },
  meter: {
    height: 8,
    borderRadius: 6,
    background: "#f1f5f9",
    marginTop: 8,
    overflow: "hidden",
  },
  bar: {
    height: "100%",
    background:
      "linear-gradient(90deg,#ef4444,#f59e0b,#10b981)",
    transition: "width .2s ease",
  },
  error: { color: "#e53e3e", fontSize: 13, marginTop: 4 },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 18,
    flexWrap: "wrap",
  },
  primaryBtn: {
    background: "rgb(255 147 92)",
    color: "white",
    border: 0,
    padding: "10px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
    marginRight: 10,
  },
  ghostBtn: {
    background: "transparent",
    border: "1px solid #e6e8ee",
    padding: "8px 12px",
    borderRadius: 8,
    color: "#6b7280",
    cursor: "pointer",
  },
};
export default ChangePassword;
